import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import ModalCloseButton from './ModalCloseButton'
import {
  clampPan,
  coverScale,
  croppedFileName,
  exportCroppedListingPhoto,
  initialCropState,
  loadImageElement,
  minCropZoom,
  type PhotoCropState,
} from '../utils/listingPhotoCrop'

interface ListingPhotoCropModalProps {
  open: boolean
  /** New upload: File. Edit existing: image URL. */
  source: File | string | null
  fileName?: string
  onConfirm: (file: File) => void
  onCancel: () => void
}

function measureViewport(el: HTMLDivElement | null): { w: number; h: number } {
  if (!el) return { w: 0, h: 0 }
  const w = el.clientWidth
  const h = el.clientHeight || Math.round(w * (3 / 4))
  return { w, h: Math.max(h, 1) }
}

export default function ListingPhotoCropModal({
  open,
  source,
  fileName = 'photo.jpg',
  onConfirm,
  onCancel,
}: ListingPhotoCropModalProps) {
  const { t } = useTranslation()
  const viewportRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)
  const cropInitializedRef = useRef<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 })
  const [viewportSize, setViewportSize] = useState({ w: 0, h: 0 })
  const [crop, setCrop] = useState<PhotoCropState>(initialCropState)

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open || !source) {
      cropInitializedRef.current = null
      setPreviewUrl(null)
      setNaturalSize({ w: 0, h: 0 })
      setCrop(initialCropState())
      setError('')
      imgRef.current = null
      return
    }

    let cancelled = false
    let blobUrl: string | null = null
    setLoading(true)
    setError('')
    setCrop(initialCropState())

    void (async () => {
      try {
        if (typeof source !== 'string') {
          blobUrl = URL.createObjectURL(source)
          setPreviewUrl(blobUrl)
        } else {
          setPreviewUrl(source)
        }
        const img = await loadImageElement(source)
        if (cancelled) return
        imgRef.current = img
        setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
      } catch {
        if (!cancelled) setError(t('listing.photoCrop.loadError'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [open, source, t])

  useLayoutEffect(() => {
    if (!open) return
    setViewportSize(measureViewport(viewportRef.current))
  }, [open, previewUrl, loading])

  useEffect(() => {
    if (!open || !viewportRef.current) return
    const el = viewportRef.current
    const ro = new ResizeObserver(() => {
      setViewportSize(measureViewport(el))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [open, previewUrl])

  useEffect(() => {
    if (!open || !source || naturalSize.w < 1 || viewportSize.w < 1) return
    const key =
      typeof source === 'string'
        ? source
        : `${source.name}-${source.size}-${source.lastModified}`
    if (cropInitializedRef.current === key) return
    cropInitializedRef.current = key
    setCrop(initialCropState(naturalSize.w, naturalSize.h, viewportSize.w, viewportSize.h))
  }, [open, source, naturalSize, viewportSize])

  const minZoom =
    naturalSize.w > 0 && viewportSize.w > 0
      ? minCropZoom(naturalSize.w, naturalSize.h, viewportSize.w, viewportSize.h)
      : 1

  const displayScale =
    naturalSize.w > 0 && viewportSize.w > 0
      ? coverScale(naturalSize.w, naturalSize.h, viewportSize.w, viewportSize.h) * crop.zoom
      : 1

  const clampedPan = clampPan(
    crop.panX,
    crop.panY,
    naturalSize.w,
    naturalSize.h,
    displayScale,
    viewportSize.w,
    viewportSize.h,
  )

  const updatePan = useCallback(
    (panX: number, panY: number) => {
      if (naturalSize.w < 1 || viewportSize.w < 1) return
      const scale = coverScale(naturalSize.w, naturalSize.h, viewportSize.w, viewportSize.h) * crop.zoom
      const next = clampPan(panX, panY, naturalSize.w, naturalSize.h, scale, viewportSize.w, viewportSize.h)
      setCrop((prev) => ({ ...prev, panX: next.panX, panY: next.panY }))
    },
    [crop.zoom, naturalSize.h, naturalSize.w, viewportSize.h, viewportSize.w],
  )

  function onPointerDown(e: React.PointerEvent) {
    if (loading || saving) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { x: e.clientX, y: e.clientY, panX: crop.panX, panY: crop.panY }
  }

  function onPointerMove(e: React.PointerEvent) {
    const drag = dragRef.current
    if (!drag) return
    updatePan(drag.panX + (e.clientX - drag.x), drag.panY + (e.clientY - drag.y))
  }

  function onPointerUp() {
    dragRef.current = null
  }

  async function handleConfirm() {
    const img = imgRef.current
    if (!img || viewportSize.w < 1 || viewportSize.h < 1) return
    setSaving(true)
    setError('')
    try {
      const blob = await exportCroppedListingPhoto(img, crop, {
        width: viewportSize.w,
        height: viewportSize.h,
      })
      const file = new File([blob], croppedFileName(fileName), { type: 'image/jpeg' })
      onConfirm(file)
    } catch {
      setError(t('listing.photoCrop.saveError'))
    } finally {
      setSaving(false)
    }
  }

  async function handleUseOriginal() {
    if (!source) return
    setSaving(true)
    setError('')
    try {
      if (typeof source === 'string') {
        const res = await fetch(source, { credentials: 'include' })
        if (!res.ok) throw new Error('fetch failed')
        const blob = await res.blob()
        onConfirm(new File([blob], fileName, { type: blob.type || 'image/jpeg' }))
        return
      }
      onConfirm(source)
    } catch {
      setError(t('listing.photoCrop.saveError'))
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  const drawW = naturalSize.w * displayScale
  const drawH = naturalSize.h * displayScale
  const left = viewportSize.w / 2 + clampedPan.panX - drawW / 2
  const top = viewportSize.h / 2 + clampedPan.panY - drawH / 2

  return createPortal(
    <div className="listing-photo-crop" role="dialog" aria-modal="true" aria-labelledby="listing-photo-crop-title">
      <div className="listing-photo-crop__backdrop" onClick={onCancel} />
      <div className="listing-photo-crop__panel">
        <ModalCloseButton
          className="listing-photo-crop__close"
          onClick={onCancel}
          aria-label={t('common.cancel')}
        />
        <h3 id="listing-photo-crop-title" className="listing-photo-crop__title">
          {t('listing.photoCrop.title')}
        </h3>
        <p className="listing-photo-crop__hint">{t('listing.photoCrop.hintOptional')}</p>

        <div
          ref={viewportRef}
          className="listing-photo-crop__viewport"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {loading && <p className="listing-photo-crop__loading">{t('listing.photoCrop.loading')}</p>}
          {!loading && previewUrl && viewportSize.w > 0 && (
            <img
              src={previewUrl}
              alt=""
              className="listing-photo-crop__image"
              draggable={false}
              style={{
                width: drawW,
                height: drawH,
                left,
                top,
              }}
            />
          )}
          <div className="listing-photo-crop__frame" aria-hidden="true">
            <span className="listing-photo-crop__frame-corner listing-photo-crop__frame-corner--tl" />
            <span className="listing-photo-crop__frame-corner listing-photo-crop__frame-corner--tr" />
            <span className="listing-photo-crop__frame-corner listing-photo-crop__frame-corner--bl" />
            <span className="listing-photo-crop__frame-corner listing-photo-crop__frame-corner--br" />
          </div>
        </div>

        <label className="listing-photo-crop__zoom">
          <span>{t('listing.photoCrop.zoom')}</span>
          <input
            type="range"
            min={minZoom}
            max={3}
            step={0.02}
            value={Math.max(minZoom, crop.zoom)}
            disabled={loading || saving || naturalSize.w < 1}
            onChange={(e) => {
              const zoom = Number(e.target.value)
              setCrop((prev) => {
                const scale =
                  coverScale(naturalSize.w, naturalSize.h, viewportSize.w, viewportSize.h) * zoom
                const nextPan = clampPan(
                  prev.panX,
                  prev.panY,
                  naturalSize.w,
                  naturalSize.h,
                  scale,
                  viewportSize.w,
                  viewportSize.h,
                )
                return { zoom, panX: nextPan.panX, panY: nextPan.panY }
              })
            }}
          />
        </label>

        {error && <p className="error-text listing-photo-crop__error">{error}</p>}

        <div className="listing-photo-crop__actions">
          <button
            type="button"
            className="listing-photo-crop__btn listing-photo-crop__btn--ghost"
            disabled={saving}
            onClick={() =>
              setCrop(initialCropState(naturalSize.w, naturalSize.h, viewportSize.w, viewportSize.h))
            }
          >
            {t('listing.photoCrop.reset')}
          </button>
          <button
            type="button"
            className="listing-photo-crop__btn listing-photo-crop__btn--ghost"
            disabled={saving || loading}
            onClick={() => void handleUseOriginal()}
          >
            {t('listing.photoCrop.useOriginal')}
          </button>
          <button
            type="button"
            className="listing-photo-crop__btn listing-photo-crop__btn--ghost"
            disabled={saving}
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="listing-photo-crop__btn listing-photo-crop__btn--primary"
            disabled={loading || saving || naturalSize.w < 1 || viewportSize.w < 1}
            onClick={() => void handleConfirm()}
          >
            {saving ? t('listing.photoCrop.saving') : t('listing.photoCrop.confirmCrop')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
